var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { failure } from '@devexperts/remote-data-ts';
import { parse } from 'date-fns';
import { sequenceS } from 'fp-ts/lib/Apply';
import { array } from 'fp-ts/lib/Array';
import { left, right } from 'fp-ts/lib/Either';
import { maximum } from 'fp-ts/lib/Foldable';
import { identity } from 'fp-ts/lib/function';
import { fromNullable, none, option, some } from 'fp-ts/lib/Option';
import { contramap, ordDate, ordNumber } from 'fp-ts/lib/Ord';
import { asks } from 'fp-ts/lib/Reader';
import * as decode from 'jwt-decode';
import { from, of, pipe } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { dataUrlToBlob, readFiles, sendJson, toRemoteData, unauthorizeAxiosRequest, } from '../utils/axios';
import { switchMapRD, tapRD } from '../utils/object.utils';
export var MIN_PASSWORD_LENGTH = 8;
export var APPSUMO_TIERS = 5;
var SECOND = 1000;
var MINUTE = 60 * SECOND;
export function planNameToType(name) {
    switch (name) {
        case 'starter':
        case 'freelance':
        case 'freelance_20211031':
        case 'pro_20211031':
        case 'unlimited':
        case 'unlimited_20211031':
        case 'unlimited_early':
        case 'appsumo_1':
        case 'appsumo_2':
        case 'appsumo_3':
        case 'appsumo_4':
        case 'appsumo_5':
        case 'team':
        case 'team_plus':
        case 'pro':
        case 'enterprise':
            return some(name);
        case 'free':
            return some('starter');
        case 'pro_monthly':
            return some('pro_20211031');
    }
    return none;
}
export function planTypeToName(type) {
    switch (type) {
        case 'starter':
        case 'pro':
        case 'pro_20211031':
        case 'unlimited_early':
        case 'appsumo_1':
        case 'appsumo_2':
        case 'appsumo_3':
        case 'appsumo_4':
        case 'appsumo_5':
        case 'team':
        case 'team_plus':
        case 'enterprise':
        case 'freelance':
        case 'freelance_20211031':
        case 'unlimited':
        case 'unlimited_20211031':
            return some(type);
    }
}
export var planTypesOrder = [
    'starter',
    'unlimited_early',
    'appsumo_1',
    'appsumo_2',
    'appsumo_3',
    'appsumo_4',
    'appsumo_5',
    'freelance',
    'freelance_20211031',
    'pro_20211031',
    'pro',
    'team',
    'team_plus',
    'unlimited',
    'unlimited_20211031',
    'enterprise',
];
export var isPlanType = function (maybeType) { return planTypesOrder.includes(maybeType); };
export var planTypeOrd = contramap(function (type) { return planTypesOrder.indexOf(type); }, ordNumber);
export var planOrd = contramap(function (plan) { return plan.type; }, planTypeOrd);
export var isPaymentAuthorizationRequired = function (current, prospective) {
    return planTypeOrd.compare(prospective, current) >= 0;
};
export var getPlanInfo = pipe(map(function (profile) {
    return profile
        .toOption()
        .chain(identity)
        .chain(function (profile) { return planNameToType(profile.billing.plan_name); })
        .getOrElse('starter');
}), distinctUntilChanged(), map(getPlan));
export var PLAN_PRO = {
    type: 'pro_20211031',
    isTrial: false,
    isActive: true,
    monthlyPrice: 25,
    yearlyPrice: 19 * 12,
    features: {
        maxNotes: none,
        maxProjects: none,
        maxTeamMembers: some(3),
        useMentions: true,
        useExport: true,
        removeWidgetBranding: false,
        restrictWidgetToMembersOnly: false,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_PRO_NEW = {
    type: 'pro',
    isTrial: false,
    isActive: true,
    monthlyPrice: 29,
    yearlyPrice: 23 * 12,
    features: {
        maxNotes: none,
        maxProjects: some(25),
        maxTeamMembers: none,
        useMentions: true,
        useExport: true,
        removeWidgetBranding: true,
        restrictWidgetToMembersOnly: true,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_TEAM = {
    type: 'team',
    isTrial: false,
    isActive: true,
    monthlyPrice: 79,
    yearlyPrice: 63 * 12,
    features: {
        maxNotes: none,
        maxProjects: some(25),
        maxTeamMembers: none,
        useMentions: true,
        useExport: true,
        removeWidgetBranding: true,
        restrictWidgetToMembersOnly: true,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_TEAM_PLUS = {
    type: 'team_plus',
    isTrial: false,
    isActive: true,
    monthlyPrice: 85,
    yearlyPrice: 85 * 12,
    features: {
        maxNotes: none,
        maxProjects: none,
        maxTeamMembers: none,
        useMentions: true,
        useExport: true,
        removeWidgetBranding: false,
        restrictWidgetToMembersOnly: false,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_ENTERPRISE = {
    type: 'enterprise',
    isTrial: false,
    isActive: true,
    monthlyPrice: 85,
    yearlyPrice: 85 * 12,
    features: {
        maxNotes: none,
        maxProjects: none,
        maxTeamMembers: none,
        useMentions: true,
        useExport: true,
        removeWidgetBranding: false,
        restrictWidgetToMembersOnly: false,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_STARTER = {
    type: 'starter',
    isTrial: false,
    isActive: true,
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: {
        maxNotes: some(15),
        maxProjects: some(1),
        maxTeamMembers: none,
        useMentions: true,
        useExport: true,
        removeWidgetBranding: false,
        restrictWidgetToMembersOnly: false,
        maxVideoDuration: 30 * SECOND,
    },
};
export var PLAN_FREELANCE_20211031 = {
    type: 'freelance_20211031',
    isTrial: false,
    isActive: true,
    monthlyPrice: 10,
    yearlyPrice: 8 * 12,
    features: {
        maxNotes: none,
        maxProjects: some(25),
        maxTeamMembers: none,
        useMentions: true,
        useExport: false,
        removeWidgetBranding: false,
        restrictWidgetToMembersOnly: false,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_FREELANCE = {
    type: 'freelance',
    isTrial: false,
    isActive: true,
    monthlyPrice: 29,
    yearlyPrice: 23 * 12,
    features: {
        maxNotes: none,
        maxProjects: some(25),
        maxTeamMembers: none,
        useMentions: true,
        useExport: true,
        removeWidgetBranding: true,
        restrictWidgetToMembersOnly: true,
        maxVideoDuration: 5 * MINUTE,
    },
};
export var PLAN_UNLIMITED_EARLY = __assign(__assign({}, PLAN_PRO), { type: 'unlimited_early', monthlyPrice: 10, yearlyPrice: 10 * 12 });
export var PLAN_UNLIMITED_20211031 = __assign(__assign({}, PLAN_PRO), { features: __assign(__assign({}, PLAN_PRO.features), { maxTeamMembers: none, removeWidgetBranding: true, restrictWidgetToMembersOnly: true }), type: 'unlimited_20211031', monthlyPrice: 99, yearlyPrice: 79 * 12 });
export var PLAN_UNLIMITED = {
    isActive: true,
    isTrial: false,
    features: {
        maxNotes: none,
        maxProjects: none,
        maxTeamMembers: none,
        removeWidgetBranding: true,
        restrictWidgetToMembersOnly: true,
        useExport: true,
        useMentions: true,
        maxVideoDuration: 5 * MINUTE,
    },
    type: 'unlimited',
    monthlyPrice: 190,
    yearlyPrice: 152 * 12,
};
export var APPSUMO_PLANS = [3, 6, 15, 30, 50].reduce(function (plans, _members, tier) {
    var _a;
    return (__assign(__assign({}, plans), (_a = {}, _a["appsumo_" + (tier + 1)] = __assign(__assign({}, PLAN_PRO), { type: "appsumo_" + (tier + 1), monthlyPrice: 0, yearlyPrice: 0, features: __assign(__assign({}, PLAN_PRO.features), { maxTeamMembers: none, removeWidgetBranding: true, restrictWidgetToMembersOnly: true, maxVideoDuration: 30 * SECOND }) }), _a)));
}, {});
export function parsePaymentMethod(p) {
    return fromNullable(p.billing.card_details).chain(function (raw) {
        return sequenceS(option)({
            brand: some(raw.brand),
            last4: some(raw.last4),
            expMonth: fromNullable(raw.exp_month),
            expYear: fromNullable(raw.exp_year),
        });
    });
}
var AuthService = /** @class */ (function () {
    function AuthService(tokenService, axios) {
        this.tokenService = tokenService;
        this.axios = axios;
        this.userId$ = this.tokenService.decoded$.pipe(map(function (maybeToken) { return maybeToken.map(function (token) { return token.user_id; }); }));
    }
    AuthService.prototype.getProfile = function () {
        return toRemoteData(this.axios.get("/api/account"));
    };
    AuthService.prototype.getProspect = function (id) {
        return toRemoteData(this.axios.get("/api/prospects/" + id));
    };
    AuthService.prototype.confirmEmail = function (userId, token) {
        return toRemoteData(this.axios.post("/api/account/activate/" + userId + "/" + token));
    };
    AuthService.prototype.sendResetToken = function (email) {
        return toRemoteData(this.axios.post('/api/account/pwforgot', { email: email.toLowerCase() }));
    };
    AuthService.prototype.resetPassword = function (userId, token, password) {
        return toRemoteData(this.axios.post("/api/account/pwforgot/reset/" + userId + "/" + token, { new_password: password }));
    };
    AuthService.prototype.resendConfirmationEmail = function () {
        return toRemoteData(this.axios.post("/api/account/confirm"));
    };
    AuthService.prototype.updatePassword = function (oldPassword, newPassword) {
        return toRemoteData(this.axios.post("/api/account/pwreset", {
            old_password: oldPassword,
            new_password: newPassword,
        }));
    };
    AuthService.prototype.saveProfile = function (profile) {
        var _this = this;
        var data = new FormData();
        ['name', 'role', 'lead', 'signup_survey_displayed'].forEach(function (key) {
            if (typeof profile[key] !== 'undefined') {
                data.append(key, String(profile[key]));
            }
        });
        profile.email && data.append('email', profile.email.toLowerCase());
        if (profile.newPassword) {
            data.append('password', profile.newPassword);
        }
        var avatar$ = profile.avatar ? from(readFiles([profile.avatar])) : of([]);
        return avatar$.pipe(switchMap(function (files) {
            if (files.length) {
                data.append("avatar", dataUrlToBlob(files[0].data), files[0].name);
            }
            return toRemoteData(_this.axios.patch("/api/account", data, { headers: { 'Content-Type': 'multipart/form-data' } }));
        }));
    };
    AuthService.prototype.saveNotifications = function (notification) {
        var _this = this;
        return toRemoteData(this.axios.patch("/api/account/notifications", notification)).pipe(switchMapRD(function () { return _this.getProfile(); }));
    };
    AuthService.prototype.switchToAnotherPaidPlan = function (data, billingPeriod, couponName) {
        var payload = __assign(__assign({}, (data.paymentMethod ? data.paymentMethod : {})), { plan_name: planTypeToName(data.planType).toNullable(), interval: billingPeriod });
        if (couponName) {
            payload.coupon_name = couponName;
        }
        return toRemoteData(this.axios.patch('/api/subscriptions', payload));
    };
    AuthService.prototype.savePaymentMethod = function (data) {
        var payload = {
            payment_method_id: data.source,
            card_details: {
                brand: data.card_brand,
                last4: data.card_last4,
                exp_month: data.card_exp_month,
                exp_year: data.card_exp_year,
            },
        };
        return toRemoteData(this.axios.post('/api/payment-methods', payload));
    };
    AuthService.prototype.switchFromFreeToPaidPlan = function (planType, billingPeriod, couponName) {
        var _this = this;
        return planTypeToName(planType)
            .map(function (planName) {
            var payload = { plan_name: planName, interval: billingPeriod };
            if (couponName) {
                payload.coupon_name = couponName;
            }
            return payload;
        })
            .foldL(function () { return of(failure(new Error('Unknown plan type ' + planType))); }, function (payload) { return toRemoteData(_this.axios.post('/api/subscriptions', payload)); });
    };
    AuthService.prototype.switchToFreePlan = function () {
        return toRemoteData(this.axios.delete('/api/subscriptions'));
    };
    /**
     * @param code use `none` to restack using codes entered previously, or `some` to apply a new code
     */
    AuthService.prototype.stackAppSumoCode = function (code) {
        var _this = this;
        return toRemoteData(code.foldL(function () { return _this.axios.post('/api/appsumo/restack'); }, function (code) { return _this.axios.post('/api/appsumo/stack', { code: code }); }));
    };
    AuthService.prototype.verifyAppSumoCode = function (code) {
        return toRemoteData(this.axios.post('/api/appsumo/verify', { code: code }));
    };
    AuthService.prototype.unstackAppSumoPlan = function () {
        return toRemoteData(this.axios.post('/api/appsumo/unstack'));
    };
    AuthService.prototype.register = function (data) {
        var _this = this;
        var email = data.email, password = data.password, fullName = data.fullName, signupType = data.signupType, joinHash = data.joinHash, _a = data.appSumoCodes, appSumoCodes = _a === void 0 ? [] : _a, referral_details = data.referral_details;
        var hashPart = joinHash ? "/" + joinHash : '';
        var request = {
            email: email.toLowerCase(),
            password: password,
            name: fullName,
        };
        var registerResult$;
        if (appSumoCodes.length > 0) {
            registerResult$ = toRemoteData(this.axios.post("/api/appsumo/redeem", __assign(__assign({}, request), { codes: appSumoCodes }), {
                transformRequest: [unauthorizeAxiosRequest, sendJson],
            }));
        }
        else {
            registerResult$ = toRemoteData(this.axios.post("/api/account/signup" + hashPart, __assign(__assign({}, request), { signup_type: signupType, referral_details: referral_details !== null && referral_details !== void 0 ? referral_details : undefined }), {
                transformRequest: [unauthorizeAxiosRequest, sendJson],
            }));
        }
        return registerResult$.pipe(switchMapRD(function () { return _this.login(email, password); }));
    };
    AuthService.prototype.login = function (email, password) {
        var _this = this;
        var result = toRemoteData(this.axios.post('/api/account/login', {
            email: email.toLowerCase(),
            password: password,
        }, {
            transformRequest: [unauthorizeAxiosRequest, sendJson],
        })).pipe(tapRD(function (tokens) { return _this.tokenService.acceptTokens(some(tokens.access), some(tokens.refresh)); }));
        return result;
    };
    AuthService.prototype.signOut = function () {
        this.tokenService.acceptTokens(none, none);
    };
    return AuthService;
}());
export { AuthService };
export function getPlan(type) {
    switch (type) {
        case 'starter':
            return PLAN_STARTER;
        case 'freelance':
            return PLAN_FREELANCE;
        case 'freelance_20211031':
            return PLAN_FREELANCE_20211031;
        case 'pro':
            return PLAN_PRO_NEW;
        case 'pro_20211031':
            return PLAN_PRO;
        case 'unlimited_early':
            return PLAN_UNLIMITED_EARLY;
        case 'unlimited':
            return PLAN_UNLIMITED;
        case 'unlimited_20211031':
            return PLAN_UNLIMITED_20211031;
        case 'appsumo_1':
        case 'appsumo_2':
        case 'appsumo_3':
        case 'appsumo_4':
        case 'appsumo_5':
            return APPSUMO_PLANS[type];
        case 'team':
            return PLAN_TEAM;
        case 'team_plus':
            return PLAN_TEAM_PLUS;
        case 'enterprise':
            return PLAN_ENTERPRISE;
    }
}
export var decodeProspectHash = asks(function (ctx) {
    return function (hash) {
        var ints = ctx.hashids.decode(hash);
        return ints.length === 3
            ? right({
                productId: ints[0],
                prospectId: ints[1],
                teamId: ints[2],
            })
            : left(new Error('Invalid share hash: [' + ints.join(',') + ']'));
    };
});
export var isSuperUser = function (_profile) { return false; };
// profile.exists(p => p.exists(p => !!p.is_superuser));	// TODO: cleanup
export function getAppSumoTier(plan) {
    var match = /^appsumo_(\d+)$/.exec(plan);
    return match ? Number(match[1]) : null;
}
export function isAppSumoPlan(plan) {
    return getAppSumoTier(plan) !== null;
}
var ordCouponRedemption = contramap(function (x) { return (x.coupon_end ? parse(x.coupon_end) : new Date('2099-01-01')); }, ordDate);
/**
 * Calculates the real price that the user is paying for their plan.
 */
export var getEffectiveRegularPriceForUser = function (plan, billingInfo) {
    var basePrice = typeof billingInfo.plan_amount === 'number'
        ? billingInfo.plan_amount / 100
        : billingInfo.interval === 'month'
            ? getPlan(plan).monthlyPrice
            : getPlan(plan).yearlyPrice;
    var activeCoupons = (billingInfo.coupon_redemptions || []).filter(function (c) { return c.active && c.plan_name === billingInfo.plan_name; });
    var bestCoupon = maximum(array, ordCouponRedemption)(activeCoupons);
    return bestCoupon
        .mapNullable(function (c) { return coupons.find(function (coupon) { return coupon.code === c.coupon_name; }); })
        .fold(basePrice, function (coupon) { return Math.round(basePrice * (1 - coupon.discountPercent / 100) * 100) / 100; });
};
export var offers = {
    blackFri2020: {
        coupon: 'black_friday_2020',
        plan: __assign(__assign({}, PLAN_PRO), { monthlyPrice: 9 }),
    },
};
export function getJwtExpiration(token) {
    try {
        var decoded = decode(token);
        if (decoded && typeof decoded.exp === 'number') {
            return new Date(decoded.exp * 1000);
        }
    }
    catch (e) {
        /* skip to `return undefined` */
    }
    return undefined;
}
export var coupons = [
    {
        // applicableToPlans: ['pro', 'freelance'],
        code: 'ONEMONTHFREE',
        name: '100% OFF',
        forceBlllingCycle: some('month'),
        numPeriods: 1,
        discountPercent: 100,
    },
    {
        // applicableToPlans: ['pro', 'freelance'],
        code: 'VIPSAVINGS',
        forceBlllingCycle: some('month'),
        numPeriods: 'forever',
        discountPercent: 32,
    },
];
//# sourceMappingURL=auth.service.js.map